"use client";

import React, {FC, useEffect, useRef, useState} from "react";
import {Button} from "@/shared/components/ui/button";
import {Input} from "@/shared/components/ui/input";
import {Search, X} from "lucide-react";
import {searchClientsCRM} from "@/features/controllers/StatusChangeController";
import {ClientCRM, getClientIdentLabel} from "@/features/types/StatusChangeInterface";

// Funkcja pomocnicza do wyświetlania nazwy klienta (z fallbackami)
const getClientDisplayName = (client: ClientCRM): string => {
    // Priorytet: f_crm_o_name > f_crm_z_clt > f_crm_r_clt_ident > f_crm_r_phone_1
    const shortName = client.f_crm_o_name?.trim();
    const contactPerson = client.f_crm_z_clt?.trim();
    const ident = client.f_crm_r_clt_ident?.trim();
    const mobilePhone = client.f_crm_r_phone_1?.trim();

    if (shortName) {
        return shortName;
    }
    if (contactPerson) {
        return contactPerson;
    }
    if (ident) {
        return ident;
    }
    if (mobilePhone) {
        return mobilePhone;
    }

    return "Brak danych";
};

interface ClientSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (client: ClientCRM) => void;
}

export const ClientSearchModal: FC<ClientSearchModalProps> = ({
                                                                  isOpen,
                                                                  onClose,
                                                                  onSelect,
                                                              }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [clients, setClients] = useState<ClientCRM[]>([]);
    const [loading, setLoading] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen) {
            setSearchQuery("");
            setClients([]);
            return;
        }

        const delayDebounce = setTimeout(() => {
            if (searchQuery.trim().length >= 2) {
                setLoading(true);
                searchClientsCRM(searchQuery)
                    .then((data) => {
                        setClients(data);
                    })
                    .catch((error) => {
                        console.error("Błąd podczas wyszukiwania klientów:", error);
                        setClients([]);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } else {
                setClients([]);
            }
        }, 300);

        return () => clearTimeout(delayDebounce);
    }, [searchQuery, isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
            <div ref={modalRef} className="bg-white p-6 rounded-lg shadow-lg w-[500px] max-h-[60vh] flex flex-col">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Wyszukaj klienta</h3>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="h-8 w-8 p-0"
                    >
                        <X size={18}/>
                    </Button>
                </div>

                <div className="mb-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18}/>
                        <Input
                            type="text"
                            placeholder="Szukaj po NIP/PESEL, skrócie nazwy, osobie kontaktowej, tel. komórkowym..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                            autoFocus
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto border rounded-lg">
                    {loading ? (
                        <div className="p-8 text-center text-gray-500">Wyszukiwanie...</div>
                    ) : clients.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            {searchQuery.trim().length < 2
                                ? "Wpisz co najmniej 2 znaki, aby rozpocząć wyszukiwanie"
                                : "Brak wyników"}
                        </div>
                    ) : (
                        <div className="divide-y">
                            {clients.map((client, index) => (
                                <div
                                    key={client.f_crm_id || `client-${index}`}
                                    className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                    onClick={() => {
                                        onSelect(client);
                                        onClose();
                                    }}
                                >
                                    <div className="font-medium mb-1">
                                        {getClientDisplayName(client)}
                                    </div>
                                    <div className="text-xs text-gray-600 space-y-0.5">
                                        {client.f_crm_c_typ_desc && (
                                            <div>Typ: {client.f_crm_c_typ_desc}</div>
                                        )}
                                        {client.f_crm_r_clt_ident && (
                                            <div>{getClientIdentLabel(client.f_crm_c_typ_desc)} {client.f_crm_r_clt_ident}</div>
                                        )}
                                        {client.f_crm_o_name && client.f_crm_o_name.trim() && (
                                            <div>Skrót nazwy: {client.f_crm_o_name}</div>
                                        )}
                                        {client.f_crm_z_clt && (
                                            <div>Osoba kontaktowa: {client.f_crm_z_clt}</div>
                                        )}
                                        {(client.f_crm_r_adr_2 || client.f_crm_l_cty) && (
                                            <div>
                                                {client.f_crm_r_adr_2 && <span>{client.f_crm_r_adr_2}</span>}
                                                {client.f_crm_r_adr_2 && client.f_crm_l_cty && <span>, </span>}
                                                {client.f_crm_l_cty && <span>{client.f_crm_l_cty}</span>}
                                            </div>
                                        )}
                                        {client.f_crm_r_phone_1 && (
                                            <div>Tel. kom.: {client.f_crm_r_phone_1}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

